import moment from "moment";
import React, { FC, useMemo, useState } from "react";
import { HiPlus } from "react-icons/hi2";
import { LuFolder } from "react-icons/lu";

import { useGetClientPortalQuery } from "../../slices/ClientPortalSlice";
import { useGetClientFilesQuery } from "../../slices/DocumentsSlice";
import { useFileOpener } from "../files/useFileOpener";
import { FileBadge } from "../portal/components/PortalUi";
import {
  filterPortalFiles,
  portalShareCandidates,
  portalTabFiles,
} from "./clientPortal.utils";
import { PortalEmptyState } from "./PortalEmptyState";
import {
  PortalFileTags,
  PortalFilterChips,
  ShareToggleButton,
  usePortalFiles,
  useRefreshClientFiles,
} from "./PortalFilesParts";
import { errorText } from "./PortalModal";
import { ShareWithClientModal } from "./ShareWithClientModal";

/**
 * "Files" tab of the client profile's Client portal widget (Roadmap 1879, owner
 * 2026-10-22: "all the files that are uploaded or sent to the client via the
 * portal"): client uploads, files shared one by one and files in a shared
 * folder. Same filters / tags / share switch as the profile Files card.
 */
export const PortalFilesSection: FC<{ customerId: string }> = ({
  customerId,
}) => {
  const { data, isLoading, error, refetch } = useGetClientFilesQuery(
    customerId,
    { skip: !customerId },
  );
  const { data: portal } = useGetClientPortalQuery(
    { customerId },
    { skip: !customerId },
  );
  const refreshAll = useRefreshClientFiles();
  const [shareOpen, setShareOpen] = useState(false);
  const [openError, setOpenError] = useState("");
  const { openFile, preview } = useFileOpener(setOpenError);

  const files = useMemo(() => portalTabFiles(data?.files ?? []), [data]);
  const candidates = useMemo(
    () => portalShareCandidates(data?.files ?? []),
    [data],
  );
  const portalFiles = usePortalFiles(customerId, files, refetch);
  const shown = filterPortalFiles(files, portalFiles.filter);
  const archived = !!portal?.archived;
  // Owner's design 2026-10-28: an empty tab shows only the empty box.
  const empty = !isLoading && !error && files.length === 0;

  return (
    <>
      <div className={"flex flex-col gap-3"}>
        <div className={empty ? "hidden" : "flex items-center gap-3"}>
          <span
            className={
              "min-w-0 flex-1 text-[12.5px] font-medium text-[#6B7185]"
            }>
            Files the client sees in the portal: their uploads and what you
            share
          </span>
          <button
            type={"button"}
            onClick={() => setShareOpen(true)}
            disabled={!data || archived}
            title={
              archived
                ? "This client is archived. Unarchive it to share files."
                : undefined
            }
            className={
              "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-3 text-[12.5px] font-bold text-[#3A4256] hover:bg-[#F4F2FE] disabled:opacity-50"
            }>
            <HiPlus size={14} /> Share files
          </button>
        </div>

        {openError && (
          <span className={"text-[13px] font-semibold text-red-500"}>
            {openError}
          </span>
        )}

        {isLoading ? (
          <span className={"text-[13px] text-[#6B7185]"}>Loading…</span>
        ) : error ? (
          <span className={"text-[13px] font-semibold text-red-500"}>
            {errorText(error, "The files couldn't be loaded.")}
          </span>
        ) : files.length ? (
          <div>
            <PortalFilterChips
              filter={portalFiles.filter}
              setFilter={portalFiles.setFilter}
              counts={portalFiles.counts}
              total={files.length}
            />
            <div className={"flex flex-col gap-2"}>
              {shown.map((file) => (
                <div
                  key={file.id}
                  className={
                    "flex items-center gap-2.5 rounded-[10px] border border-[#EDEFF3] px-3 py-2.5 hover:bg-[#FAFAFC]"
                  }>
                  <button
                    type={"button"}
                    title={`Open ${file.name}`}
                    onClick={() => void openFile(file.item)}
                    className={
                      "flex min-w-0 flex-1 items-center gap-2.5 text-left"
                    }>
                    <FileBadge name={file.name} />
                    <div className={"min-w-0 flex-1"}>
                      <div
                        className={
                          "truncate text-[13.5px] font-bold text-[#1F2433]"
                        }>
                        {file.name}
                      </div>
                      <div className={"text-xs font-medium text-[#9097A8]"}>
                        {file.addedAt
                          ? moment(file.addedAt).format("MMM D, YYYY")
                          : ""}
                      </div>
                      <PortalFileTags
                        file={file}
                        isNew={portalFiles.newIds.has(file.id)}
                      />
                      {file.sharedFolder && (
                        <div
                          title={
                            "The client sees this file because its folder is shared. Change it on the Files tab."
                          }
                          className={
                            "mt-1 flex min-w-0 items-center gap-1 text-[11px] font-semibold text-[#6B7185]"
                          }>
                          <LuFolder size={12} className={"shrink-0"} />
                          <span className={"truncate"}>
                            In shared folder: {file.sharedFolder}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                  {!file.sharedFolder && (
                    <ShareToggleButton
                      file={file}
                      disabled={portalFiles.isSharing}
                      onToggle={() => void portalFiles.toggleShare(file)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <PortalEmptyState
            testId={"portal-files-empty"}
            icon={<LuFolder size={22} />}
            title={"Nothing in the portal yet"}
            text={
              "Files you share and files the client uploads will appear here."
            }
          />
        )}
      </div>

      {shareOpen && (
        <ShareWithClientModal
          customerId={customerId}
          clientName={data?.name}
          files={candidates}
          onClose={() => setShareOpen(false)}
          onDone={refreshAll}
        />
      )}
      {preview}
    </>
  );
};
